import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

function loadEmailJS() {
  return new Promise((resolve) => {
    if (window.emailjs) { resolve(); return }
    const s = document.createElement('script')
    s.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js'
    s.onload = () => {
      const keys = JSON.parse(localStorage.getItem('emailjs_keys') || '{}')
      if (keys.publicKey) window.emailjs.init(keys.publicKey)
      resolve()
    }
    document.head.appendChild(s)
  })
}

async function sendInvoiceEmail({ to, clientName, invoiceNum, amount, date, description, zelle, companyName, companyPhone }) {
  await loadEmailJS()
  const keys = JSON.parse(localStorage.getItem('emailjs_keys') || '{}')
  if (!keys.serviceId || !keys.templateId || !keys.publicKey) throw new Error('EmailJS not configured. Go to Settings → Email to set up.')
  return window.emailjs.send(keys.serviceId, keys.templateId, {
    to_email: to, to_name: clientName, invoice_num: invoiceNum,
    amount: `$${parseFloat(amount).toFixed(2)}`,
    date: new Date(date+'T12:00:00').toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}),
    description, zelle: zelle||'', company_name: companyName||"Kitty's Cleaning Services", company_phone: companyPhone||'(310) 425-3195',
  })
}

function generateInvoicePDF({ invoiceNum, billTo, date, lineItems, discount, discountType, notes, companySettings, serviceType, isPaid, paidDate, isProposal }) {
  const subtotal = lineItems.reduce((s,li) => s + (parseFloat(li.qty)||0)*(parseFloat(li.rate)||0), 0)
  const discountAmt = discountType==='percent' ? subtotal*(parseFloat(discount)||0)/100 : parseFloat(discount)||0
  const total = Math.max(0, subtotal - discountAmt)
  const co = companySettings || {}
  const docLabel = isProposal ? 'Estimate' : isPaid ? 'Receipt' : 'Invoice'
  const themeColor = isProposal ? '#185fa5' : isPaid ? '#1d9e75' : '#e8547a'
  const fmtLong = d => new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})
  const paidDateFmt = paidDate ? fmtLong(paidDate) : null
  const validUntil = (() => { const d = new Date(date+'T12:00:00'); d.setDate(d.getDate()+30); return d.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) })()
  const money = n => '$' + (Number(n)||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})
  const coName = co.name || "Kitty's Cleaning Services"
  const coAddr = co.address || '6427 Wilcox Ave, Bell, CA 90201'
  const coPhone = co.phone || '(310) 425-3195'
  const coEmail = co.email || 'info@kittyscleaningservices.com'

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${docLabel} ${invoiceNum} - ${billTo.name||''}</title>
<style>
* { box-sizing:border-box; margin:0; padding:0; }
@page { size:letter; margin:16mm 16mm 18mm; }
body { font-family:Arial,Helvetica,sans-serif; color:#202124; font-size:12px; line-height:1.55; max-width:760px; margin:0 auto; padding:36px 28px; background:#fff; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
@media print { body { padding:0; } }
.head { display:flex; justify-content:space-between; align-items:flex-end; padding-bottom:14px; border-bottom:3px solid ${themeColor}; }
.brand { font-size:21px; font-weight:700; letter-spacing:.2px; }
.contact { font-size:10.5px; color:#5f6368; margin-top:4px; }
.doc { text-align:right; }
.doc-title { font-size:22px; font-weight:300; letter-spacing:.18em; text-transform:uppercase; color:${themeColor}; }
.doc-num { font-size:11px; color:#5f6368; margin-top:2px; }
.meta { display:grid; grid-template-columns:1.2fr 1fr; gap:32px; margin:26px 0 24px; }
.label { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.12em; color:#80868b; margin-bottom:6px; }
.bill-name { font-size:15px; font-weight:700; margin-bottom:2px; }
.bill-detail { color:#5f6368; }
.facts td { padding:3px 0; vertical-align:top; }
.facts td.k { color:#80868b; padding-right:16px; white-space:nowrap; }
.facts td.v { font-weight:500; text-align:right; }
.status { display:inline-block; margin-top:10px; font-size:9.5px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:${themeColor}; border:1.5px solid ${themeColor}; border-radius:4px; padding:2px 8px; }
table.items { width:100%; border-collapse:collapse; }
table.items th { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#5f6368; text-align:left; padding:9px 10px; background:#f1f3f4; border-bottom:1px solid #dadce0; }
table.items td { padding:10px; border-bottom:1px solid #e8eaed; vertical-align:top; }
table.items .r { text-align:right; white-space:nowrap; }
table.items tr { page-break-inside:avoid; }
.totals { display:flex; justify-content:flex-end; margin-top:14px; }
.totals table { min-width:260px; border-collapse:collapse; }
.totals td { padding:6px 10px; }
.totals td.r { text-align:right; }
.totals tr.sub td { color:#5f6368; }
.totals tr.grand td { border-top:2px solid #202124; font-size:15px; font-weight:700; padding-top:10px; }
.totals tr.grand td.r { color:${themeColor}; font-size:18px; }
.section { margin-top:26px; page-break-inside:avoid; }
.section h3 { font-size:9.5px; font-weight:700; text-transform:uppercase; letter-spacing:.12em; color:#202124; padding-bottom:5px; border-bottom:1px solid #dadce0; margin-bottom:8px; }
.section p { color:#3c4043; margin-bottom:4px; }
.stamp { position:absolute; top:140px; right:60px; border:3px solid #1d9e75; color:#1d9e75; font-size:24px; font-weight:800; padding:4px 16px; border-radius:6px; transform:rotate(-10deg); opacity:.8; letter-spacing:.12em; }
.foot { margin-top:34px; padding-top:10px; border-top:1px solid #dadce0; display:flex; justify-content:space-between; font-size:10px; color:#80868b; }
</style></head><body>
<div style="position:relative">
${isPaid ? '<div class="stamp">PAID</div>' : ''}
<div class="head">
  <div>
    <div class="brand">${coName}</div>
    <div class="contact">${coAddr} &nbsp;·&nbsp; ${coPhone} &nbsp;·&nbsp; ${coEmail}</div>
  </div>
  <div class="doc">
    <div class="doc-title">${docLabel}</div>
    <div class="doc-num">No. ${invoiceNum}</div>
  </div>
</div>

<div class="meta">
  <div>
    <div class="label">${isProposal ? 'Prepared for' : 'Bill to'}</div>
    <div class="bill-name">${billTo.name||''}</div>
    <div class="bill-detail">${[billTo.address, billTo.city, billTo.phone, billTo.email].filter(Boolean).join('<br>')}</div>
  </div>
  <div>
    <table class="facts" style="margin-left:auto">
      <tr><td class="k">${docLabel} date</td><td class="v">${fmtLong(date)}</td></tr>
      <tr><td class="k">Service</td><td class="v">${serviceType||'Cleaning Service'}</td></tr>
      ${isProposal
        ? `<tr><td class="k">Valid until</td><td class="v">${validUntil}</td></tr>`
        : isPaid
          ? `<tr><td class="k">Paid</td><td class="v">${paidDateFmt || 'In full'}</td></tr>`
          : `<tr><td class="k">Payment</td><td class="v">Due on receipt</td></tr>`}
      <tr><td class="k">${isProposal?'Estimated total':isPaid?'Total paid':'Amount due'}</td><td class="v" style="color:${themeColor};font-weight:700">${money(total)}</td></tr>
    </table>
    <div style="text-align:right"><span class="status">${isProposal?'Estimate · not a bill':isPaid?'Paid in full':'Due on receipt'}</span></div>
  </div>
</div>

<table class="items">
  <thead><tr>
    <th style="width:52%">Description</th>
    <th style="width:14%">Date</th>
    <th class="r" style="width:8%">Qty</th>
    <th class="r" style="width:12%">Rate</th>
    <th class="r" style="width:14%">Amount</th>
  </tr></thead>
  <tbody>
    ${lineItems.map(li => `<tr>
      <td>${li.description||''}</td>
      <td>${li.date ? new Date(li.date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : ''}</td>
      <td class="r">${li.qty||1}</td>
      <td class="r">${money(li.rate)}</td>
      <td class="r">${money((parseFloat(li.qty)||0)*(parseFloat(li.rate)||0))}</td>
    </tr>`).join('')}
  </tbody>
</table>

<div class="totals"><table>
  <tr class="sub"><td>Subtotal</td><td class="r">${money(subtotal)}</td></tr>
  ${discountAmt>0 ? `<tr class="sub"><td>Discount${discountType==='percent'?' ('+discount+'%)':''}</td><td class="r">-${money(discountAmt)}</td></tr>` : ''}
  <tr class="sub"><td>Tax</td><td class="r">${money(0)}</td></tr>
  <tr class="grand"><td>${isProposal?'Estimated total':isPaid?'Total paid':'Total due'}</td><td class="r">${money(total)}</td></tr>
</table></div>

${notes ? `<div class="section"><h3>Notes</h3><p>${notes}</p></div>` : ''}

<div class="section">
  ${isProposal ? `
    <h3>About this estimate</h3>
    <p>This is an estimate, not a bill. No payment is due yet. Pricing is valid until ${validUntil}.</p>
    <p>Once you approve, we'll schedule your service and send a formal invoice.</p>
  ` : isPaid ? `
    <h3>Payment received</h3>
    <p>Thank you! This invoice was paid in full${paidDateFmt ? ' on ' + paidDateFmt : ''}.</p>
  ` : `
    <h3>How to pay</h3>
    ${co.zelle ? `<p><strong>Zelle:</strong> ${co.zelle}</p>` : ''}
    <p><strong>Cash:</strong> accepted</p>
    <p style="color:#5f6368">Please include invoice no. ${invoiceNum} with your payment.</p>
  `}
</div>

<div class="foot">
  <span>Thank you for choosing ${coName}.</span>
  <span>${docLabel} No. ${invoiceNum}</span>
</div>
</div>
<script>window.onload=()=>window.print()</script>
</body></html>`
  const win = window.open('','_blank')
  win.document.write(html)
  win.document.close()
}

// ── CLIENT SEARCH ─────────────────────────────────────────────────────────────
function ClientSearch({ clients, value, onChange, inputStyle }) {
  const selected = clients.find(c => c.id === value)
  const [query, setQuery] = useState(selected?.name || '')
  const [open, setOpen] = useState(false)

  const filtered = query.trim()
    ? clients.filter(c => c.name.toLowerCase().includes(query.toLowerCase()) || (c.nickname||'').toLowerCase().includes(query.toLowerCase()))
    : clients

  useEffect(() => {
    if (selected) setQuery(selected.name)
  }, [value])

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        placeholder="Buscar cliente..."
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange('') }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && filtered.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1.5px solid #e3e5e8', borderRadius: 8, zIndex: 50, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }}>
          {filtered.slice(0, 20).map(c => (
            <div key={c.id}
              onMouseDown={() => { onChange(c.id); setQuery(c.name); setOpen(false) }}
              style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f1f3f4', background: c.id === value ? '#fdeef3' : '#fff', color: '#202124' }}>
              {c.name}{c.nickname ? <span style={{ color: '#9aa0a6', fontSize: 11, marginLeft: 6 }}>({c.nickname})</span> : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── SKYNOVA-STYLE INVOICE EDITOR ──────────────────────────────────────────────
function InvoiceEditor({ invoice, clients, onClose, onSaved, companySettings }) {
  const isEdit = !!invoice?.id
  const preselectClientId = invoice?._preselect_client || null
  const client = invoice?.id ? clients.find(c=>c.id===invoice.client_id) : null

  function getBillTo(cl) {
    if (!cl) return { name:'', address:'', city:'', phone:'', email:'' }
    return {
      name: cl.billing_name || cl.name || '',
      address: cl.billing_address || cl.address || '',
      city: cl.billing_city || cl.city || '',
      phone: cl.billing_phone || cl.phone || '',
      email: cl.billing_email || cl.email || '',
    }
  }

  const [clientId, setClientId] = useState(invoice?.client_id || preselectClientId || '')
  const [isProposal, setIsProposal] = useState(invoice?.status==='proposal' || invoice?._as_proposal || false)
  const [invoiceNum, setInvoiceNum] = useState(invoice?.invoice_num||'')
  const [date, setDate] = useState(invoice?.date||new Date().toISOString().slice(0,10))
  const [serviceType, setServiceType] = useState(invoice?.service_type||'Residential Cleaning')
  const [billTo, setBillTo] = useState(invoice?.id ? getBillTo(client) : {name:'',address:'',city:'',phone:'',email:''})
  const [lineItems, setLineItems] = useState(() => {
    if (invoice?.line_items) try { return JSON.parse(invoice.line_items) } catch(e) {}
    if (invoice?.amount) return [{ description: invoice.description||'Professional Cleaning Service', date: invoice.date||'', qty: 1, rate: parseFloat(invoice.amount)||0 }]
    return [{ description:'Professional Cleaning Service', date:'', qty:1, rate:0 }]
  })
  const [discount, setDiscount] = useState(invoice?.discount||'')
  const [discountType, setDiscountType] = useState(invoice?.discount_type||'none')
  const [notes, setNotes] = useState(invoice?.notes&&!invoice.notes.startsWith('NO_TEAM')?invoice.notes:'')
  const [saving, setSaving] = useState(false)

  // Month loader state
  const [loadMonth, setLoadMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`
  })
  const [loadingVisits, setLoadingVisits] = useState(false)

  async function loadVisitsFromMonth() {
    if (!clientId) { alert('Select a client first'); return }
    setLoadingVisits(true)
    const [year, mon] = loadMonth.split('-').map(Number)
    const start = `${year}-${String(mon).padStart(2,'0')}-01`
    // Get actual last day of month
    const lastDay = new Date(year, mon, 0).getDate()
    const end = `${year}-${String(mon).padStart(2,'0')}-${String(lastDay).padStart(2,'0')}`

    // Pull from both tables in parallel
    const [{ data: hist }, { data: allJobs }] = await Promise.all([
      supabase.from('cleaning_history').select('cleaned_date').eq('client_id', clientId).gte('cleaned_date', start).lte('cleaned_date', end),
      supabase.from('jobs').select('scheduled_date').eq('client_id', clientId).neq('status', 'cancelled').gte('scheduled_date', start).lte('scheduled_date', end),
    ])

    // Merge and deduplicate by date
    const allDates = new Set()
    ;(hist || []).forEach(h => allDates.add(h.cleaned_date))
    ;(allJobs || []).forEach(j => allDates.add(j.scheduled_date))

    setLoadingVisits(false)
    if (!allDates.size) { alert('No visits found for that month'); return }

    const cl = clients.find(c=>c.id===clientId)
    const rate = parseFloat(cl?.default_rate) || 0
    const items = [...allDates].sort().map(date => ({
      description: `Professional Cleaning — ${cl?.nickname||cl?.name||''}`,
      date,
      qty: 1,
      rate,
    }))
    setLineItems(items)
    setDate(end)
  }

  const subtotal = lineItems.reduce((s,li)=>s+(parseFloat(li.qty)||0)*(parseFloat(li.rate)||0),0)
  const discountAmt = discountType==='percent'?subtotal*(parseFloat(discount)||0)/100:parseFloat(discount)||0
  const total = Math.max(0, subtotal-discountAmt)

  useEffect(()=>{
    if (clientId && !isEdit) {
      const cl = clients.find(c=>c.id===clientId)
      if (cl) {
        setBillTo(getBillTo(cl))
        setServiceType(cl.is_airbnb?'Airbnb Turnover Cleaning':cl.is_commercial?'Commercial Cleaning':'Residential Cleaning')
        const initials = cl.name.split(/\s+/).map(w=>w[0]).join('').toUpperCase().slice(0,3)
        const now = new Date()
        const num = `KC-${initials}-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-001`
        setInvoiceNum(num)
        if (lineItems.length===1 && !lineItems[0].rate) {
          setLineItems([{description:`Professional Cleaning Service — ${cl.nickname||cl.name}`, date:date, qty:1, rate:parseFloat(cl.default_rate)||0}])
        }
      }
    }
  },[clientId])

  function addLine() { setLineItems(prev=>[...prev,{description:'',date:'',qty:1,rate:0}]) }
  function removeLine(i) { setLineItems(prev=>prev.filter((_,j)=>j!==i)) }
  function updateLine(i,key,val) { setLineItems(prev=>prev.map((li,j)=>j===i?{...li,[key]:val}:li)) }

  async function save(andPrint) {
    if (!clientId) { alert('Select a client'); return }
    setSaving(true)
    const payload = {
      client_id: clientId,
      invoice_num: invoiceNum,
      date,
      service_type: serviceType,
      amount: total,
      description: lineItems.map(li=>li.description).join('; '),
      line_items: JSON.stringify(lineItems),
      discount: discount||null,
      discount_type: discountType,
      notes: notes||'',
      status: isProposal ? 'proposal' : (invoice?.status||'sent'),
    }
    let saved
    if (isEdit) {
      const {data} = await supabase.from('invoices').update(payload).eq('id',invoice.id).select().single()
      saved = data
    } else {
      const {data} = await supabase.from('invoices').insert([payload]).select().single()
      saved = data
    }
    setSaving(false)
    if (andPrint) {
      generateInvoicePDF({ invoiceNum, billTo, date, lineItems, discount, discountType, notes, serviceType, companySettings, isPaid: invoice?.status==='paid', paidDate: invoice?.paid_date, isProposal })
    }
    onSaved(saved)
  }

  return (
    <div style={ed.wrap}>
      <div style={ed.header}>
        <div style={{fontSize:16,fontWeight:700,color:'#202124'}}>{isEdit?'✏️ Editar factura':'📄 Nueva factura'}</div>
        <button style={ed.closeBtn} onClick={onClose}>×</button>
      </div>
      <div style={ed.body}>
        {/* FROM */}
        <div style={ed.section}>
          <div style={ed.sectionTitle}>De (From)</div>
          <div style={ed.grid2}>
            <div style={{gridColumn:'1/-1'}}>
              <div style={ed.label}>Empresa</div>
              <div style={ed.staticVal}>{companySettings.name||"Kitty's Cleaning Services"}</div>
            </div>
            <div>
              <div style={ed.label}>Dirección</div>
              <div style={ed.staticVal}>{companySettings.address||'6427 Wilcox Ave, Bell, CA 90201'}</div>
            </div>
            <div>
              <div style={ed.label}>Teléfono / Email</div>
              <div style={ed.staticVal}>{companySettings.phone||'(310) 425-3195'} · {companySettings.email||'info@kittyscleaningservices.com'}</div>
            </div>
          </div>
        </div>

        {/* DOCUMENT TYPE TOGGLE */}
        <div style={{display:'flex',gap:8,marginBottom:16}}>
          <button type="button" onClick={()=>setIsProposal(false)}
            style={{flex:1,padding:'10px',borderRadius:10,border:'1px solid #e3e5e8',cursor:'pointer',fontWeight:600,fontSize:13,
              background:!isProposal?'#e8547a':'#fff',color:!isProposal?'#fff':'#5f6368'}}>
            🧾 Factura
          </button>
          <button type="button" onClick={()=>setIsProposal(true)}
            style={{flex:1,padding:'10px',borderRadius:10,border:'1px solid #e3e5e8',cursor:'pointer',fontWeight:600,fontSize:13,
              background:isProposal?'#185fa5':'#fff',color:isProposal?'#fff':'#5f6368'}}>
            📝 Propuesta
          </button>
        </div>

        {/* INVOICE META */}
        <div style={ed.section}>
          <div style={ed.grid3}>
            <div style={{gridColumn:'1/-1'}}>
              <div style={ed.label}>Cliente</div>
              {isEdit
                ? <div style={ed.staticVal}>{client?.name}</div>
                : <ClientSearch clients={clients} value={clientId} onChange={setClientId} inputStyle={ed.input} />
              }
            </div>
            <div>
              <div style={ed.label}># de {isProposal?'propuesta':'factura'}</div>
              <input style={ed.input} value={invoiceNum} onChange={e=>setInvoiceNum(e.target.value)}/>
            </div>
            <div>
              <div style={ed.label}>Fecha</div>
              <input type="date" style={ed.input} value={date} onChange={e=>setDate(e.target.value)}/>
            </div>
            <div>
              <div style={ed.label}>Tipo de servicio</div>
              <select style={ed.input} value={serviceType} onChange={e=>setServiceType(e.target.value)}>
                <option>Residential Cleaning</option>
                <option>Airbnb Turnover Cleaning</option>
                <option>Commercial Cleaning</option>
                <option>Deep Cleaning</option>
                <option>Move-In / Move-Out Cleaning</option>
                <option>Post-Construction Cleaning</option>
              </select>
            </div>
          </div>
        </div>

        {/* BILL TO */}
        <div style={ed.section}>
          <div style={ed.sectionTitle}>Para (Bill To)</div>
          <div style={ed.grid2}>
            <div>
              <div style={ed.label}>Nombre</div>
              <input style={ed.input} value={billTo.name} onChange={e=>setBillTo(b=>({...b,name:e.target.value}))}/>
            </div>
            <div>
              <div style={ed.label}>Email</div>
              <input style={ed.input} value={billTo.email} onChange={e=>setBillTo(b=>({...b,email:e.target.value}))}/>
            </div>
            <div>
              <div style={ed.label}>Dirección</div>
              <input style={ed.input} value={billTo.address} onChange={e=>setBillTo(b=>({...b,address:e.target.value}))}/>
            </div>
            <div>
              <div style={ed.label}>Ciudad / Estado</div>
              <input style={ed.input} value={billTo.city} onChange={e=>setBillTo(b=>({...b,city:e.target.value}))}/>
            </div>
            <div>
              <div style={ed.label}>Teléfono</div>
              <input style={ed.input} value={billTo.phone} onChange={e=>setBillTo(b=>({...b,phone:e.target.value}))}/>
            </div>
          </div>
        </div>

        {/* LOAD FROM MONTH */}
        {!isEdit && (
          <div style={{...ed.section, background:'#f8f9fa', borderRadius:10, padding:'12px 14px', border:'1px solid #e3e5e8'}}>
            <div style={ed.sectionTitle}>📅 Cargar visitas del mes</div>
            <div style={{fontSize:11,color:'#80868b',marginBottom:10}}>Selecciona un mes y se llena automáticamente con las visitas del historial.</div>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              <input type="month" value={loadMonth} onChange={e=>setLoadMonth(e.target.value)}
                style={{...ed.input,width:160,fontSize:12}}/>
              <button style={{...ed.addLineBtn,background:'#e8547a',color:'#fff',border:'none',padding:'6px 14px'}}
                onClick={loadVisitsFromMonth} disabled={loadingVisits||!clientId}>
                {loadingVisits ? 'Cargando...' : '⚡ Cargar visitas'}
              </button>
            </div>
          </div>
        )}

        {/* LINE ITEMS */}
        <div style={ed.section}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={ed.sectionTitle}>Servicios</div>
            <button style={ed.addLineBtn} onClick={addLine}>+ Agregar línea</button>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#f8f9fa'}}>
                  {['Descripción','Fecha','Qty','Rate','Total',''].map(h=>(
                    <th key={h} style={{padding:'8px 8px',textAlign:'left',fontSize:10,color:'#80868b',fontWeight:600,borderBottom:'1px solid #e3e5e8'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li,i)=>(
                  <tr key={i}>
                    <td style={{padding:'6px 4px'}}><input style={{...ed.input,width:'100%',minWidth:160}} value={li.description} onChange={e=>updateLine(i,'description',e.target.value)}/></td>
                    <td style={{padding:'6px 4px'}}><input type="date" style={{...ed.input,width:120}} value={li.date||''} onChange={e=>updateLine(i,'date',e.target.value)}/></td>
                    <td style={{padding:'6px 4px'}}><input type="number" style={{...ed.input,width:50}} min="0" step="1" value={li.qty} onChange={e=>updateLine(i,'qty',e.target.value)}/></td>
                    <td style={{padding:'6px 4px'}}><input type="number" style={{...ed.input,width:80}} min="0" step="0.01" value={li.rate} onChange={e=>updateLine(i,'rate',e.target.value)}/></td>
                    <td style={{padding:'6px 8px',fontWeight:600,color:'#202124'}}>${((parseFloat(li.qty)||0)*(parseFloat(li.rate)||0)).toFixed(2)}</td>
                    <td style={{padding:'6px 4px'}}>{lineItems.length>1&&<button style={{border:'none',background:'none',color:'#bdc1c6',cursor:'pointer',fontSize:16}} onClick={()=>removeLine(i)}>×</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
            <div style={{minWidth:240}}>
              <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',fontSize:12,color:'#5f6368',borderBottom:'1px solid #e3e5e8'}}>
                <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid #e3e5e8'}}>
                <select style={{...ed.input,flex:'0 0 auto',width:110,fontSize:11}} value={discountType} onChange={e=>setDiscountType(e.target.value)}>
                  <option value="none">Sin descuento</option>
                  <option value="percent">% Porcentaje</option>
                  <option value="flat">$ Monto fijo</option>
                </select>
                {discountType!=='none'&&<input type="number" style={{...ed.input,width:70,fontSize:11}} min="0" value={discount} onChange={e=>setDiscount(e.target.value)} placeholder="0"/>}
                {discountAmt>0&&<span style={{fontSize:11,color:'#1d9e75'}}>-${discountAmt.toFixed(2)}</span>}
              </div>
              <div style={{display:'flex',justifyContent:'space-between',padding:'10px 14px',background:'#e8547a',borderRadius:8,marginTop:8}}>
                <span style={{fontSize:14,fontWeight:700,color:'#fff'}}>Total Due</span>
                <span style={{fontSize:20,fontWeight:700,color:'#fff'}}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* NOTES */}
        <div style={ed.section}>
          <div style={ed.label}>Notas (aparecen en la factura)</div>
          <textarea style={{...ed.input,height:70,resize:'vertical',width:'100%'}}
            placeholder="Ej: Thank you for your continued business! Next cleaning scheduled for..."
            value={notes} onChange={e=>setNotes(e.target.value)}/>
        </div>

        {/* ACTIONS */}
        <div style={{display:'flex',gap:8,paddingTop:8}}>
          <button style={ed.cancelBtn} onClick={onClose}>Cancelar</button>
          <button style={ed.saveBtn} onClick={()=>save(false)} disabled={saving||!clientId}>
            {saving?'Guardando...':'💾 Guardar'}
          </button>
          <button style={{...ed.saveBtn,background:'#185fa5'}} onClick={()=>save(true)} disabled={saving||!clientId}>
            {saving?'...':'📄 Guardar y abrir PDF'}
          </button>
        </div>
      </div>
    </div>
  )
}

function AssignTeamModal({ invoice, onDone }) {
  const [staff, setStaff] = useState([])
  const [selected, setSelected] = useState([])
  const [saving, setSaving] = useState(false)
  useEffect(()=>{ supabase.from('staff').select('*').eq('is_active',true).order('name').then(({data})=>setStaff(data||[])) },[])
  async function save() {
    if (!selected.length) return
    setSaving(true)
    const staffNote = JSON.stringify({staffIds:selected,note:'assigned via factura'})
    const ex = await supabase.from('routes').select('*').eq('route_date',invoice.date).eq('label','1').maybeSingle()
    if (ex.data) await supabase.from('routes').update({notes:staffNote}).eq('id',ex.data.id)
    else await supabase.from('routes').insert([{route_date:invoice.date,label:'1',color:'#e8547a',notes:staffNote}])
    if (invoice.job_id) await supabase.from('jobs').update({route:'1'}).eq('id',invoice.job_id)
    setSaving(false); onDone()
  }
  return (
    <div>
      <div style={{fontSize:11,color:'#80868b',marginBottom:8}}>Selecciona las empleadas que fueron ese día:</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:16}}>
        {staff.map(s=>(
          <button key={s.id} onClick={()=>setSelected(p=>p.includes(s.id)?p.filter(x=>x!==s.id):[...p,s.id])}
            style={{fontSize:12,padding:'5px 12px',border:`1.5px solid ${selected.includes(s.id)?'#e8547a':'#e3e5e8'}`,borderRadius:20,
              background:selected.includes(s.id)?'#fdeef3':'#fff',color:selected.includes(s.id)?'#e8547a':'#5f6368',cursor:'pointer'}}>
            {selected.includes(s.id)?'✓ ':''}{s.name}
          </button>
        ))}
      </div>
      <button style={{width:'100%',padding:'9px',border:'none',borderRadius:8,background:selected.length?'#e8547a':'#e3e5e8',
        color:'#fff',fontWeight:600,cursor:selected.length?'pointer':'not-allowed'}}
        onClick={save} disabled={saving||!selected.length}>
        {saving?'Guardando...':'✓ Asignar equipo'}
      </button>
    </div>
  )
}

export default function Invoices() {
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [showEditor, setShowEditor] = useState(false)
  const [editingInvoice, setEditingInvoice] = useState(null)

  // Listen for "open invoice for client" event from client profile button
  useEffect(() => {
    // Check if we were opened via the profile button (stored in localStorage)
    const pending = localStorage.getItem('open_invoice_for_client')
    if (pending) {
      localStorage.removeItem('open_invoice_for_client')
      setEditingInvoice({ _preselect_client: pending })
      setShowEditor(true)
    }

    function handler(e) {
      const { clientId } = e.detail || {}
      if (clientId) {
        setEditingInvoice({ _preselect_client: clientId })
        setShowEditor(true)
      }
    }
    window.addEventListener('open-invoice-for-client', handler)
    return () => window.removeEventListener('open-invoice-for-client', handler)
  }, [])
  const [invTab, setInvTab] = useState('pending')
  const [showVoid, setShowVoid] = useState(null)
  const [assignTeamInv, setAssignTeamInv] = useState(null)
  const [voidReason, setVoidReason] = useState('')
  const [noteModal, setNoteModal] = useState(null) // invoice
  const [noteText, setNoteText] = useState('')
  const [paidModal, setPaidModal] = useState(null) // invoice
  const [paidDate, setPaidDate] = useState('')
  const [loading, setLoading] = useState(true)
  const [dbError, setDbError] = useState(false)
  const [emailSending, setEmailSending] = useState({})
  const [emailSent, setEmailSent] = useState({})
  const [companySettings, setCompanySettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem('company_settings')||'{}') } catch(e) { return {} }
  })
  const [showSettings, setShowSettings] = useState(false)

  useEffect(()=>{ fetchData() },[])

  async function fetchData() {
    setLoading(true)
    const [{data:cl},{data:inv,error:invErr}] = await Promise.all([
      supabase.from('clients').select('*').eq('is_active',true).order('name'),
      supabase.from('invoices').select('*').order('created_at',{ascending:false}),
    ])
    if (invErr) { setDbError(true); setLoading(false); return }
    setClients(cl||[]); setInvoices(inv||[]); setLoading(false)
  }

  async function markPaid(inv, datePaid) {
    const pd = datePaid || new Date().toISOString().slice(0,10)
    await supabase.from('invoices').update({status:'paid', notes: inv.notes && !inv.notes.startsWith('VOID') ? inv.notes : (inv.notes||''), paid_date: pd}).eq('id',inv.id)
    setInvoices(p=>p.map(i=>i.id===inv.id?{...i,status:'paid',paid_date:pd}:i))
    const client = clients.find(c=>c.id===inv.client_id)
    await supabase.from('financials').insert([{
      type:'income',
      category:'Limpieza residencial',
      amount:parseFloat(inv.amount),
      description:`Factura ${inv.invoice_num} pagada — ${client?.name||'Cliente'}`,
      entry_date: pd,
    }])
  }

  async function saveNote() {
    if (!noteModal) return
    await supabase.from('invoices').update({notes: noteText}).eq('id', noteModal.id)
    setInvoices(p=>p.map(i=>i.id===noteModal.id?{...i,notes:noteText}:i))
    setNoteModal(null); setNoteText('')
  }

  async function savePaidDate() {
    if (!paidModal || !paidDate) return
    await supabase.from('invoices').update({paid_date: paidDate}).eq('id', paidModal.id)
    // Also update the financials entry_date
    await supabase.from('financials').update({entry_date: paidDate})
      .ilike('description', `%${paidModal.invoice_num}%`)
    setInvoices(p=>p.map(i=>i.id===paidModal.id?{...i,paid_date:paidDate}:i))
    setPaidModal(null); setPaidDate('')
  }

  async function markVoid(id, reason) {
    await supabase.from('invoices').update({status:'void',notes:reason?`VOID: ${reason}`:'VOID'}).eq('id',id)
    setInvoices(p=>p.map(i=>i.id===id?{...i,status:'void',notes:reason?`VOID: ${reason}`:'VOID'}:i))
    setShowVoid(null); setVoidReason('')
  }

  function openEditor(inv=null, asProposal=false) {
    setEditingInvoice(asProposal && !inv ? { _as_proposal: true } : inv)
    setShowEditor(true)
  }

  function handleSaved(saved) {
    if (editingInvoice?.id) setInvoices(p=>p.map(i=>i.id===saved.id?saved:i))
    else setInvoices(p=>[saved,...p])
    setShowEditor(false); setEditingInvoice(null)
  }

  async function convertToInvoice(inv) {
    await supabase.from('invoices').update({ status:'sent' }).eq('id', inv.id)
    setInvoices(p=>p.map(i=>i.id===inv.id?{...i,status:'sent'}:i))
  }

  const filtered = invoices.filter(i=>{
    if (invTab==='paid') return i.status==='paid'
    if (invTab==='void') return i.status==='void'
    if (invTab==='review') return i.status==='pending_review'
    if (invTab==='proposal') return i.status==='proposal'
    return i.status==='sent'||i.status==='pending'
  })

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <div style={{fontSize:15,fontWeight:600}}>🧾 Facturas</div>
        <a href="https://drive.google.com/drive/folders/1iP-akxqhDAc2OFDLTRVgv2Et0_F9T5Al"
          target="_blank" rel="noreferrer"
          style={{fontSize:12,padding:'6px 12px',border:'1px solid #4285f4',borderRadius:8,background:'#e8f0fe',color:'#1a73e8',textDecoration:'none',fontWeight:500}}>
          📁 Drive
        </a>
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <button style={s.settBtn} onClick={()=>setShowSettings(true)}>⚙️ Mi empresa</button>
          <button style={{...s.addBtn,background:'#185fa5'}} onClick={()=>openEditor(null,true)}>+ Nueva propuesta</button>
          <button style={s.addBtn} onClick={()=>openEditor()}>+ Nueva factura</button>
        </div>
      </div>

      <div style={s.body}>
        {dbError ? (
          <div style={{padding:32,textAlign:'center'}}>
            <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
            <div style={{fontSize:15,fontWeight:600,marginBottom:8}}>La tabla de facturas no existe aún</div>
            <div style={{fontSize:13,color:'#5f6368',marginBottom:16}}>Ve a Supabase → SQL Editor y corre add_invoices.sql</div>
            <button style={{padding:'8px 16px',background:'#e8547a',color:'#fff',border:'none',borderRadius:8,cursor:'pointer'}} onClick={fetchData}>Reintentar</button>
          </div>
        ) : loading ? <div style={s.loading}>Cargando...</div> : (<>

          {/* ── Summary cards ── */}
          {(() => {
            const pending = invoices.filter(i => i.status === 'sent' || i.status === 'pending')
            const paid = invoices.filter(i => i.status === 'paid')
            const pendingTotal = pending.reduce((a, i) => a + (parseFloat(i.amount) || 0), 0)
            const paidTotal = paid.reduce((a, i) => a + (parseFloat(i.amount) || 0), 0)
            const overdueCount = pending.filter(i => i.date && new Date(i.date) < new Date()).length
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#fff5f8', border: '1.5px solid #fcd0dc', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#e8547a', marginBottom: 6 }}>⏳ Pendiente</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#202124' }}>${pendingTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 3 }}>{pending.length} factura{pending.length !== 1 ? 's' : ''}{overdueCount > 0 ? ` · ${overdueCount} vencida${overdueCount !== 1 ? 's' : ''}` : ''}</div>
                </div>
                <div style={{ background: '#f0fdf4', border: '1.5px solid #a5d6a7', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#1d9e75', marginBottom: 6 }}>✅ Cobrado</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#202124' }}>${paidTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 3 }}>{paid.length} factura{paid.length !== 1 ? 's' : ''} pagada{paid.length !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ background: '#f8f9fa', border: '1.5px solid #e3e5e8', borderRadius: 12, padding: '14px 16px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: '#80868b', marginBottom: 6 }}>📊 Total</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#202124' }}>${(pendingTotal + paidTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 3 }}>{invoices.filter(i => i.status !== 'void').length} facturas en total</div>
                </div>
              </div>
            )
          })()}

          <div style={{display:'flex',gap:4,marginBottom:14,flexWrap:'wrap'}}>
            {[['review','🔴 Por revisar'],['pending','⏳ Pendientes'],['paid','✅ Pagadas'],['proposal','📝 Propuestas'],['void','🚫 Anuladas']].map(([v,l])=>{
              const count = invoices.filter(i=>i.status===v||(v==='pending'&&i.status==='sent')||(v==='review'&&i.status==='pending_review')).length
              return (
                <button key={v} style={{fontSize:12,padding:'6px 14px',border:'1px solid #e3e5e8',borderRadius:8,cursor:'pointer',
                  background:invTab===v?(v==='proposal'?'#185fa5':'#e8547a'):'#fff',color:invTab===v?'#fff':'#5f6368',fontWeight:invTab===v?500:400}}
                  onClick={()=>setInvTab(v)}>
                  {l}{count>0&&<span style={{fontSize:10,marginLeft:4}}>({count})</span>}
                </button>
              )
            })}
          </div>

          {filtered.length===0&&<div style={s.empty}><div style={{fontSize:36,marginBottom:8}}>🧾</div><div>Sin facturas en esta sección</div></div>}

          {filtered.map(inv=>{
            const client = clients.find(c=>c.id===inv.client_id)
            const isPaid = inv.status==='paid'
            const isVoid = inv.status==='void'
            const isReview = inv.status==='pending_review'
            const isProposal = inv.status==='proposal'
            const billTo = {
              name: client?.billing_name||client?.name||'',
              address: client?.billing_address||client?.address||'',
              city: client?.billing_city||client?.city||'',
              phone: client?.billing_phone||client?.phone||'',
              email: client?.billing_email||client?.email||'',
            }
            const lineItems = (() => { try { return JSON.parse(inv.line_items||'null') } catch(e) { return null } })()
              || [{ description: inv.description||'Professional Cleaning Service', date: inv.date, qty:1, rate: parseFloat(inv.amount)||0 }]

            return (
              <div key={inv.id} style={{...s.invCard,opacity:isVoid?.6:1,
                borderLeft:isPaid?'3px solid #1d9e75':isVoid?'3px solid #80868b':isReview?'3px solid #dc2626':isProposal?'3px solid #185fa5':'3px solid #e8547a',
                background:isReview?'#fff5f5':'#fff',
              }}>
                <div style={s.invHead}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'#202124'}}>{client?.name||'Cliente'}</div>
                    <div style={{fontSize:11,color:'#80868b'}}>{inv.invoice_num} · {new Date(inv.date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric',year:'numeric'})}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:16,fontWeight:700,color:isPaid?'#1d9e75':isVoid?'#80868b':isReview?'#dc2626':isProposal?'#185fa5':'#e8547a'}}>${parseFloat(inv.amount).toFixed(2)}</div>
                    <div style={{fontSize:10,fontWeight:500,color:isPaid?'#1d9e75':isVoid?'#80868b':isReview?'#dc2626':isProposal?'#185fa5':'#f59e0b'}}>
                      {isPaid?'✅ Pagada':isVoid?'🚫 Anulada':isReview?'🔴 Por revisar':isProposal?'📝 Propuesta':'⏳ Pendiente'}
                    </div>
                  </div>
                </div>
                {!isVoid&&<div style={{fontSize:11,color:'#80868b',marginTop:4,fontStyle:'italic'}}>{inv.description?.slice(0,80)}{inv.description?.length>80?'...':''}</div>}
                {isVoid&&inv.notes&&<div style={{fontSize:11,color:'#80868b',marginTop:4,fontStyle:'italic'}}>{inv.notes}</div>}
                <div style={{display:'flex',gap:6,marginTop:8,flexWrap:'wrap'}}>
                  {isReview ? (<>
                    <button style={{...s.reprintBtn,background:'#dcfce7',color:'#166534',borderColor:'#86efac',fontWeight:700,fontSize:13}}
                      onClick={async()=>{
                        await supabase.from('invoices').update({status:'pending'}).eq('id',inv.id)
                        setInvoices(p=>p.map(i=>i.id===inv.id?{...i,status:'pending'}:i))
                      }}>✓ Aprobar</button>
                    <button style={{...s.reprintBtn,background:'#fee2e2',color:'#991b1b',borderColor:'#fca5a5',fontWeight:700,fontSize:13}}
                      onClick={async()=>{
                        await supabase.from('invoices').update({status:'void',notes:'Auto-rechazada'}).eq('id',inv.id)
                        setInvoices(p=>p.map(i=>i.id===inv.id?{...i,status:'void',notes:'Auto-rechazada'}:i))
                      }}>✕ Rechazar</button>
                    <button style={{...s.reprintBtn,background:'#e8f0fe',color:'#185fa5',borderColor:'#c5d9f5'}}
                      onClick={()=>openEditor(inv)}>✏️ Editar</button>
                  </>) : (<>
                  <button style={s.reprintBtn} onClick={()=>generateInvoicePDF({invoiceNum:inv.invoice_num,billTo,date:inv.date,lineItems,discount:inv.discount,discountType:inv.discount_type||'none',notes:inv.notes,serviceType:inv.service_type,companySettings,isPaid:inv.status==='paid',paidDate:inv.paid_date,isProposal})}>
                    📄 PDF
                  </button>
                  <button style={{...s.reprintBtn,background:'#e8f0fe',color:'#185fa5',borderColor:'#c5d9f5'}}
                    onClick={()=>openEditor(inv)}>
                    ✏️ Editar
                  </button>
                  {!isPaid&&!isVoid&&!isReview&&!isProposal&&client?.email&&(
                    <button style={{...s.reprintBtn,background:emailSent[inv.id]?'#e1f5ee':'#e8f0fe',color:emailSent[inv.id]?'#085041':'#1a73e8',borderColor:emailSent[inv.id]?'#5DCAA5':'#4285f4'}}
                      disabled={emailSending[inv.id]}
                      onClick={async()=>{
                        setEmailSending(p=>({...p,[inv.id]:true}))
                        try { await sendInvoiceEmail({to:client.email,clientName:client.name,invoiceNum:inv.invoice_num,amount:inv.amount,date:inv.date,description:inv.description,zelle:companySettings.zelle,companyName:companySettings.name,companyPhone:companySettings.phone}); setEmailSent(p=>({...p,[inv.id]:true})) }
                        catch(e) { alert('Error: '+e.message) }
                        setEmailSending(p=>({...p,[inv.id]:false}))
                      }}>
                      {emailSending[inv.id]?'Enviando...':emailSent[inv.id]?'✓ Enviado':'📧 Email'}
                    </button>
                  )}
                  {isProposal&&(
                    <button style={{...s.reprintBtn,background:'#e1f5ee',color:'#085041',borderColor:'#5DCAA5',fontWeight:700}}
                      onClick={()=>convertToInvoice(inv)}>
                      ✓ Convertir a factura
                    </button>
                  )}
                  {!isPaid&&!isVoid&&!isReview&&!isProposal&&(
                    <button style={{...s.reprintBtn,background:'#e1f5ee',color:'#085041',borderColor:'#5DCAA5'}} onClick={()=>markPaid(inv)}>✅ Pagada</button>
                  )}
                  {isPaid&&(
                    <button style={{...s.reprintBtn,background:'#f0f9ff',color:'#0369a1',borderColor:'#bae6fd'}} 
                      onClick={()=>{setPaidModal(inv);setPaidDate(inv.paid_date||inv.date||'')}}>
                      📅 {inv.paid_date ? inv.paid_date : 'Fecha pago'}
                    </button>
                  )}
                  <button style={{...s.reprintBtn,background:'#fafafa',color:'#5f6368',borderColor:'#e0e0e0'}}
                    onClick={()=>{setNoteModal(inv);setNoteText(inv.notes&&!inv.notes.startsWith('VOID')?inv.notes:'')}}>
                    {inv.notes&&!inv.notes.startsWith('VOID')&&!inv.notes.startsWith('Auto')? '📝 Nota' : '+ Nota'}
                  </button>
                  {inv.notes==='NO_TEAM_ASSIGNED'&&!isVoid&&!isReview&&(
                    <button style={{...s.reprintBtn,background:'#fff8e1',color:'#854d0e',borderColor:'#fde68a'}} onClick={()=>setAssignTeamInv(inv)}>👩 Asignar equipo</button>
                  )}
                  {!isVoid&&!isReview&&(
                    <button style={{...s.reprintBtn,background:'#f1f3f4',color:'#80868b',borderColor:'#ddd'}} onClick={()=>setShowVoid(inv.id)}>🚫 Anular</button>
                  )}
                  </>)}
                </div>
              </div>
            )
          })}
        </>)}
      </div>

      {/* INVOICE EDITOR */}
      {showEditor&&(
        <div style={s.overlay} onClick={()=>setShowEditor(false)}>
          <div style={{...s.modal,width:'min(95vw,780px)',maxHeight:'92vh',padding:0,overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
            <InvoiceEditor invoice={editingInvoice} clients={clients} onClose={()=>setShowEditor(false)} onSaved={handleSaved} companySettings={companySettings}/>
          </div>
        </div>
      )}

      {/* ASSIGN TEAM */}
      {assignTeamInv&&(
        <div style={s.overlay} onClick={()=>setAssignTeamInv(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>👩 Asignar equipo</div>
            <AssignTeamModal invoice={assignTeamInv} onDone={()=>{
              setAssignTeamInv(null)
              supabase.from('invoices').update({notes:''}).eq('id',assignTeamInv.id)
              setInvoices(p=>p.map(i=>i.id===assignTeamInv.id?{...i,notes:''}:i))
            }}/>
          </div>
        </div>
      )}

      {/* VOID */}
      {noteModal&&(
        <div style={s.overlay} onClick={()=>setNoteModal(null)}>
          <div style={{...s.modal,maxWidth:380}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>📝 Nota — {clients.find(c=>c.id===noteModal.client_id)?.name}</div>
            <textarea style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,resize:'vertical',height:80,fontFamily:'inherit'}}
              value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Nota personal (no se muestra al cliente)..."/>
            <div style={{display:'flex',gap:8,marginTop:10}}>
              <button style={{...s.mSave,background:'#5f6368',flex:1}} onClick={()=>setNoteModal(null)}>Cancelar</button>
              <button style={{...s.mSave,flex:1}} onClick={saveNote}>Guardar</button>
            </div>
          </div>
        </div>
      )}
      {paidModal&&(
        <div style={s.overlay} onClick={()=>setPaidModal(null)}>
          <div style={{...s.modal,maxWidth:340}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:600,fontSize:14,marginBottom:12}}>📅 Fecha de pago — {clients.find(c=>c.id===paidModal.client_id)?.name}</div>
            <input type="date" style={{width:'100%',padding:'8px 10px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13}}
              value={paidDate} onChange={e=>setPaidDate(e.target.value)}/>
            <div style={{display:'flex',gap:8,marginTop:10}}>
              <button style={{...s.mSave,background:'#5f6368',flex:1}} onClick={()=>setPaidModal(null)}>Cancelar</button>
              <button style={{...s.mSave,flex:1}} onClick={savePaidDate}>Guardar</button>
            </div>
          </div>
        </div>
      )}
      {showVoid&&(
        <div style={s.overlay} onClick={()=>setShowVoid(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>🚫 Anular factura</div>
            <div style={{fontSize:12,color:'#5f6368',marginBottom:12}}>Queda en el registro como anulada.</div>
            <div style={s.mfield}>
              <label style={s.mlabel}>Razón (opcional)</label>
              <input style={s.minput} value={voidReason} placeholder="Ej: error en monto, cliente canceló..." onChange={e=>setVoidReason(e.target.value)}/>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button style={s.mCancel} onClick={()=>setShowVoid(null)}>Cancelar</button>
              <button style={{...s.mSave,background:'#5f6368'}} onClick={()=>markVoid(showVoid,voidReason)}>Anular</button>
            </div>
          </div>
        </div>
      )}

      {/* COMPANY SETTINGS */}
      {showSettings&&(
        <div style={s.overlay} onClick={()=>setShowSettings(false)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTitle}>⚙️ Info de mi empresa</div>
            <div style={{fontSize:11,color:'#9aa0a6',marginBottom:12}}>Aparece en todas las facturas</div>
            {[['name','Nombre empresa'],['address','Dirección'],['phone','Teléfono'],['email','Email'],['zelle','Zelle']].map(([k,l])=>(
              <div key={k} style={s.mfield}>
                <label style={s.mlabel}>{l}</label>
                <input style={s.minput} value={companySettings[k]||''} onChange={e=>{
                  const u={...companySettings,[k]:e.target.value}
                  setCompanySettings(u); localStorage.setItem('company_settings',JSON.stringify(u))
                }}/>
              </div>
            ))}
            <button style={{...s.mSave,width:'100%'}} onClick={()=>setShowSettings(false)}>Guardar</button>
          </div>
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:8,padding:'12px 16px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  addBtn:{fontSize:12,padding:'6px 14px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer'},
  settBtn:{fontSize:12,padding:'6px 12px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer'},
  body:{flex:1,overflowY:'auto',padding:'14px 16px'},
  loading:{textAlign:'center',color:'#9aa0a6',padding:32,fontSize:13},
  empty:{textAlign:'center',padding:48,color:'#80868b',fontSize:14},
  invCard:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',padding:'14px 16px',marginBottom:10},
  invHead:{display:'flex',justifyContent:'space-between',alignItems:'flex-start'},
  reprintBtn:{fontSize:11,padding:'5px 10px',border:'1px solid #dadce0',borderRadius:6,background:'#fdeef3',color:'#c03060',cursor:'pointer'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100},
  modal:{background:'#fff',borderRadius:14,padding:'20px 22px',width:400,maxHeight:'85vh',overflowY:'auto',boxShadow:'0 8px 32px rgba(0,0,0,0.15)'},
  modalTitle:{fontSize:15,fontWeight:600,color:'#202124',marginBottom:14},
  mfield:{marginBottom:12},
  mlabel:{fontSize:11,color:'#80868b',display:'block',marginBottom:3},
  minput:{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa',color:'#202124'},
  mCancel:{flex:1,padding:'8px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer',fontSize:12},
  mSave:{flex:2,padding:'8px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer',fontSize:12},
}

const ed = {
  wrap:{display:'flex',flexDirection:'column',height:'100%'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid #e3e5e8',flexShrink:0},
  closeBtn:{fontSize:22,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6'},
  body:{overflowY:'auto',padding:'16px 20px',flex:1},
  section:{marginBottom:20,paddingBottom:20,borderBottom:'1px solid #e3e5e8'},
  sectionTitle:{fontSize:11,fontWeight:700,color:'#e8547a',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10},
  grid2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12},
  grid3:{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12},
  label:{fontSize:10,color:'#80868b',marginBottom:3,fontWeight:500},
  input:{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:7,fontSize:12,background:'#f8f9fa',color:'#202124',fontFamily:'inherit'},
  staticVal:{fontSize:12,color:'#5f6368',padding:'7px 0'},
  addLineBtn:{fontSize:11,padding:'4px 10px',border:'1px solid #e8547a',borderRadius:6,background:'#fdeef3',color:'#e8547a',cursor:'pointer'},
  cancelBtn:{padding:'9px 16px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#5f6368',cursor:'pointer',fontSize:12},
  saveBtn:{padding:'9px 16px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:600,cursor:'pointer',fontSize:12},
}
